<template>
  <!-- Edit Activity Modal -->
  <div :id="id" class="modal custom-modal fade" role="dialog">
    <div class="modal-dialog modal-dialog-centered modal-md" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Edit Activity</h5>
          <button type="button" ref="Close" class="btn-close" data-bs-dismiss="modal" aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="modal-body">
          <div v-if="activityDetails?.is_manually_edited" class="alert alert-info py-2 px-3 mb-3 d-flex align-items-center" style="font-size: 0.85rem;">
            <i class="fas fa-info-circle me-2"></i>
            <div>
              <span>Last edited </span>
              <span v-if="activityDetails.updated_by_user">
                by <strong>{{ activityDetails.updated_by_user.first_name }} {{ activityDetails.updated_by_user.last_name }}</strong>
              </span>
              <span v-else-if="activityDetails.updated_by && activityDetails.updated_by.first_name">
                by <strong>{{ activityDetails.updated_by.first_name }} {{ activityDetails.updated_by.last_name }}</strong>
              </span>
              <span v-else-if="activityDetails.updated_by">
                by <strong>{{ activityDetails.updated_by }}</strong>
              </span>
              <span v-if="activityDetails.updated_at">
                at <strong>{{ formatDateTime(activityDetails.updated_at) }}</strong>
              </span>
              <span v-if="activityDetails.reason">
                <br/><small class="text-muted">Reason: {{ activityDetails.reason }}</small>
              </span>
            </div>
          </div>
          <form @submit.prevent="submit()">
             <!-- Employee Selection -->
            <div class="input-block mb-1" v-if="isReassignable">
              <label class="col-form-label">Employee <span class="text-danger">*</span></label>
              <v-autocomplete 
                v-model="selectedEmployee" 
                :items="employeeOptions" 
                
                variant="outlined"
                density="comfortable"
                item-title="text" 
                item-value="id"
                clearable
              ></v-autocomplete>
            </div>

            <!-- Activity Type -->
            <div class="input-block mb-3">
              <label class="col-form-label">Activity Type <span class="text-danger">*</span></label>
              <select v-model="activityType" class="form-select" required>
                <option value="">Select Type</option>
                <option value="Check In">Check In</option>
                <option value="Check Out">Check Out</option>
                <option value="Break Out">Break Out</option>
                <option value="Break In">Break In</option>
              </select>
            </div>

            <!-- Date & Time -->
            <div class="input-block mb-3">
              <label class="col-form-label">Date <span class="text-danger">*</span></label>
              <div class="input-block form-focus position-relative">
                <div class="cal-icon" style="z-index: 90;">
                  <date-picker 
                    v-model="activityDate" 
                    placeholder="Select Date" 
                    class="form-control floating datetimepicker"
                    :editable="true" 
                    :clearable="false" 
                    :inputFormat="dateFormat" 
                  />
                </div>
              </div>
            </div>

            <div class="input-block mb-3">
              <label class="col-form-label">Time <span class="text-danger">*</span></label>
              <input type="text" maxlength="8" @keydown="handleTimeKeydown($event, $data, 'activityTime')" v-model="activityTime" class="form-control timepicker" placeholder="00:00:00" />
            </div>

            <!-- Location (Optional) -->
            <div class="input-block mb-3">
              <label class="col-form-label">Location</label>
              <select v-model="locationId" class="form-select">
                <option value="">Select Location (Optional)</option>
                <option v-for="location in locationOptions" :key="location.id" :value="location.id">
                  {{ location.text }}
                </option>
              </select>
            </div>

            <!-- Reason -->
            <div class="input-block mb-3">
              <label class="col-form-label">Reason <span class="text-danger" v-if="!isAdmin">*</span></label>
              <textarea 
                v-model="reason" 
                class="form-control" 
                rows="2" 
                placeholder="Enter reason for editing activity"
              ></textarea>
            </div>

            <div class="submit-section">
              <button type="submit" class="btn btn-primary submit-btn" :disabled="loader">
                <span v-if="!loader">Update</span>
                <div class="spinner-border spinner-border-sm text-light" role="status" v-else>
                  <span class="visually-hidden">Loading...</span>
                </div>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
  <!-- /Edit Activity Modal -->
</template>

<script>
import { timeInputMixin } from "../../mixins/timeInputMixin";
import axios from 'axios';
import { VUE_APP_API_BASE_URL } from "../../config";
import { getActivityTypeLabel, formatDateTimeStandardWithTime } from '../../utils/util-funcitons';

export default {
  mixins: [timeInputMixin],
  props: {
    id: {
      type: String,
      default: 'edit_activity'
    }
  },
  emits: ['activityUpdated'],
  data() {
    return {
      activityDetails: null,
      activityId: '',
      activityType: '',
      locationId: '',
      latitude: '',
      longitude: '',
      loader: false,
      locationOptions: [],
      employeeOptions: [],
      employeeName: '',
      selectedEmployee: null,
      isReassignable: false,
      activityDate: null,
      activityTime: '00:00:00',
      dateFormat: 'dd/MM/yyyy',
      reason: ''
    };
  },
  computed: {
    // Combine date and time into activityDateTime string for API
    activityDateTime() {
      if (!this.activityDate) return '';
      const date = new Date(this.activityDate);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day} ${this.activityTime || '00:00:00'}`;
    },
    isAdmin() {
      const userInfo = JSON.parse(localStorage.getItem('userInfo'));
      return userInfo && userInfo.is_admin === 1;
    }
  },
  mounted() {
    this.getLocationIds();
    this.getEmployees();
  },
  methods: {
    getActivityTypeLabel,
    formatDateTime(datetime) {
      if (!datetime) return '';
      return formatDateTimeStandardWithTime(datetime);
    },
    async getLocationIds() {
      try {
        const response = await axios.get(`${VUE_APP_API_BASE_URL}/location/all`);
        if (response.data.status) {
          this.locationOptions = response.data.data.map(element => ({
            id: element.id,
            text: element.name
          }));
        }
      } catch (error) {
        console.error(error);
      }
    },

    async getEmployees() {
      try {
        const response = await axios.get(`${VUE_APP_API_BASE_URL}/user/user-list`);
        if (response.data.status) {
          this.employeeOptions = response.data.data.map(element => ({
            id: element.id,
            text: `${element.first_name} ${element.last_name}`
          }));
        }
      } catch (error) {
        console.error(error);
      }
    },

    submit() {
      if (!this.activityType) {
        this.$toast.error('Please select an activity type');
        return;
      }

      // If reassignable, require employee selection
      if (this.isReassignable && !this.selectedEmployee) {
        this.$toast.error('Please select an employee to assign this activity to');
        return;
      }

      if (!this.isAdmin && (!this.reason || this.reason.trim() === '')) {
        this.$toast.error('Please enter a reason');
        return;
      }

      this.loader = true;
      
      const payload = {
        id: this.activityId,
        type: this.getActivityTypeLabel(this.activityType),
        reason: this.reason.trim()
      };

      // Add user_id if activity is reassignable
      if (this.isReassignable && this.selectedEmployee) {
        payload.user_id = this.selectedEmployee;
      }

      // Add optional fields if provided
      if (this.activityDateTime) {
        // activityDateTime is always a string in YYYY-MM-DD HH:mm:ss format
        payload.created_at = this.activityDateTime;
      }
      if (this.locationId) {
        payload.location_id = this.locationId;
      }
      if (this.latitude) {
        payload.latitude = this.latitude;
      }
      if (this.longitude) {
        payload.longitude = this.longitude;
      }

      axios.post(`${VUE_APP_API_BASE_URL}/activity/edit`, payload)
        .then((response) => {
          if (response.data.status) {
            this.$toast.success(response.data.message || 'Activity updated successfully');
            this.$refs.Close.click();
            this.$emit('activityUpdated');
          } else {
            this.$toast.error(response.data.message || response.data.errors || 'Failed to update activity');
          }
        })
        .catch((error) => {
          const errorMessage = error.response?.data?.message || 
                              error.response?.data?.errors || 
                              'Something went wrong';
          this.$toast.error(errorMessage);
          console.error(error);
        })
        .finally(() => {
          this.loader = false;
        });
    },

    updateValues(record) {
      this.activityDetails = record;
      this.activityId = record.id;
      this.activityType = getActivityTypeLabel(record.type);
      this.locationId = record.location_id || '';
      this.latitude = record.latitude || '';
      this.longitude = record.longitude || '';
      this.reason = record.reason || '';
      this.selectedEmployee = record.user_id || null;
      
      // Allow reassignment for normal employee activities and unrecognized activities
      this.isReassignable = !record.visitor_id && !record.contractor_id;
      
      // Parse the created_at date into separate date and time
      if (record.created_at) {
        
        let datePart = '';
        let timePart = '00:00';

        // Robust parsing: search for DD/MM/YYYY or YYYY-MM-DD
        const dateMatch = record.created_at.match(/(\d{2}\/\d{2}\/\d{4})|(\d{4}-\d{2}-\d{2})/);
        if (dateMatch) {
          datePart = dateMatch[0];
          
          // Also try to find time HH:mm or HH:mm:ss
          const timeMatch = record.created_at.match(/(\d{2}:\d{2}(?::\d{2})?)/);
          if (timeMatch) {
            timePart = timeMatch[0];
          }
          
          if (datePart.includes('/')) {
            const [day, month, year] = datePart.split('/').map(Number);
            this.activityDate = new Date(year, month - 1, day);
          } else {
            this.activityDate = new Date(datePart);
          }
        } else {
          // Fallback
          this.activityDate = new Date(record.created_at);
        }

        if (isNaN(this.activityDate.getTime())) {
          this.activityDate = null;
        }

        this.activityTime = timePart.length === 5 ? timePart + ':00' : timePart;
      }

      // Get employee name
      if (record.user) {
        this.employeeName = `${record.user.first_name} ${record.user.last_name}`;
      } else if (record.visitor) {
        this.employeeName = `${record.visitor.first_name} ${record.visitor.last_name}`;
      } else if (record.contractor) {
        this.employeeName = `${record.contractor.first_name} ${record.contractor.last_name}`;
      } else {
        this.employeeName = 'Unknown';
      }
    },

    resetForm() {
      this.activityDetails = null;
      this.activityId = '';
      this.activityType = '';
      this.activityDate = null;
      this.activityTime = '00:00:00';
      this.locationId = '';
      this.latitude = '';
      this.longitude = '';
      this.employeeName = '';
      this.selectedEmployee = null;
      this.isReassignable = false;
    }
  }
};
</script>

<style scoped>
.alert-info {
  background-color: #e7f3ff;
  border-color: #b6d4fe;
  color: #084298;
}

.alert-warning {
  background-color: #fff3cd;
  border-color: #ffecb5;
  color: #664d03;
}

/* Consistent styling for Ant Design date and time pickers */
:deep(.ant-picker) {
  width: 100%;
  height: 44px;
  border-radius: 4px;
  border: 1px solid #ced4da;
}

:deep(.ant-picker:hover),
:deep(.ant-picker-focused) {
  border-color: #86b7fe;
}

:deep(.ant-picker-input > input) {
  font-size: 14px;
}
</style>
